import dayjs from "dayjs";
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  LocationActivityLog,
  LocationActivityLogType,
} from "../../../../../models/LocationActivityLog";
import DataSvc from "../../../../../services/dataSvc";
import LocalPostItem from "../../../../DashboardComponents/LocalPostsComponent/LocalPostItem";
import { RecentMediaListContext } from "../../../../DashboardComponents/RecentPostsList";
import ModalConfirm from "../../../../ModalConfirm";

import "./styles.scss";

interface LocationActivityTableDetailsSectionProps {
  isHeader?: boolean;
  activityLog?: LocationActivityLog;
}

export const LocationActivityTableDetailsSection: React.FunctionComponent<
  LocationActivityTableDetailsSectionProps
> = ({ isHeader, activityLog }) => {
  const { t: _t, i18n } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`locationActivityPage.locationActivityTable.${key}`),
    [_t]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState<LocationActivityLog | null>(
    null
  );

  const isSpecialType =
    activityLog?.type === LocationActivityLogType.PICKUP_FIELD_CHANGE ||
    activityLog?.type === LocationActivityLogType.HEALTH_INDEX_CHANGE;

  const handleModalClose = useCallback(
    () => setModalOpen(false),
    [setModalOpen]
  );

  const handleItemClick = useCallback(async () => {
    if (!isSpecialType && activityLog?.id) {
      try {
        if (
          activityLog.type === LocationActivityLogType.NEW_REVIEW ||
          activityLog.type === LocationActivityLogType.REVIEW_REPLY ||
          activityLog.type === LocationActivityLogType.NEW_LOCAL_POST ||
          activityLog.type === LocationActivityLogType.NEW_MEDIA
        ) {
          const details = await DataSvc.getLogDetail(
            activityLog.location.id,
            activityLog.id
          );
          setModalDetails(details);
        } else {
          setModalDetails(activityLog.details);
        }
        setModalOpen(true);
      } catch (error) {
        setModalDetails(activityLog.details);
        setModalOpen(true);
      }
    }
  }, [isSpecialType, activityLog, setModalDetails, setModalOpen]);

  const renderModalContent = useCallback(() => {
    if (!activityLog || !modalDetails) {
      return (
        <div className="modal-details-json">{t("no_details_available")}</div>
      );
    }

    switch (activityLog.type) {
      case LocationActivityLogType.NEW_MEDIA:
        if (modalDetails.relatedContent?.url) {
          return (
            <div className="modal-media-content">
              <img
                src={modalDetails.relatedContent.url}
                alt={`Media from ${modalDetails.location?.name || "location"}`}
              />
            </div>
          );
        }
        break;

      case LocationActivityLogType.NEW_LOCAL_POST:
        if (modalDetails.relatedContent) {
          return (
            <div className="modal-localpost-content">
              <LocalPostItem
                localPostItem={modalDetails.relatedContent as any}
                isShowLocationName={true}
                context={RecentMediaListContext.OWN_LOCATION_HISTORY}
              />
            </div>
          );
        }
        break;

      case LocationActivityLogType.NEW_REVIEW:
      case LocationActivityLogType.REVIEW_REPLY:
        if (modalDetails.relatedContent) {
          return (
            <div className="modal-review-content">
              <div className="review-item">
                <div>
                  {modalDetails.relatedContent?.reviewer_name && (
                    <div className="reviewer-name">
                      {t("reviewer_name")}{" "}
                      {modalDetails.relatedContent.reviewer_name}
                    </div>
                  )}
                </div>
                <div className="top-container">
                  <div className="rating">
                    {Array.from(
                      { length: modalDetails.relatedContent.rating ?? 0 },
                      (_, i) => (
                        <span key={i}>⭐️</span>
                      )
                    )}
                  </div>
                  <div className="review-date">
                    {dayjs(modalDetails.relatedContent.review_date).format(
                      "YYYY-MM-DD"
                    )}
                  </div>
                </div>
                <div className="comment">
                  {modalDetails.relatedContent.comment}
                </div>
                {activityLog.type === LocationActivityLogType.REVIEW_REPLY &&
                  modalDetails.relatedContent.review_reply && (
                    <div className="review-reply-section">
                      <div className="reply-title">{t("owner_reply")}:</div>
                      <div className="reply-content">
                        {modalDetails.relatedContent.review_reply}
                      </div>
                      {modalDetails.relatedContent.review_reply_update_time && (
                        <div className="reply-date">
                          {dayjs(
                            modalDetails.relatedContent.review_reply_update_time
                          ).format("YYYY-MM-DD")}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          );
        }
        break;
    }

    return (
      <div className="modal-details-json">
        {typeof modalDetails === "string"
          ? modalDetails
          : JSON.stringify(modalDetails, null, 2)}
      </div>
    );
  }, [activityLog, modalDetails, t]);

  const renderPickupFieldChangeText = (details: any) => {
    if (!details || typeof details !== "object") return t("view_details");

    const { field } = details;
    if (!field) return t("view_details");

    const fieldTranslation = i18n.t(
      `facilityDetailsPage.pickupFields.${field}`
    );

    return (
      <>
        {t("details.pickup_field_changed")}({fieldTranslation}):{" "}
        {t(`details.${details["new_value"]}`)}
      </>
    );
  };

  const renderHealthIndexChangeText = (details: any) => {
    if (!details || typeof details !== "object") return t("view_details");

    const { old_value, new_value } = details;
    if (!old_value || !new_value) return t("view_details");

    const oldIndex = old_value.charAt(0);
    const newIndex = new_value.charAt(0);

    return (
      <>
        {t("details.health_index_changed")}
        {oldIndex}→{newIndex}
      </>
    );
  };

  if (isHeader) {
    return (
      <div className="details-column details-header">{t("header.details")}</div>
    );
  }

  return (
    <>
      <div
        className={`details-column details-cell${
          isSpecialType ? " fixed-width no-link" : ""
        }`}
        onClick={handleItemClick}
        title={
          typeof activityLog?.details === "string"
            ? activityLog.details
            : JSON.stringify(activityLog?.details)
        }
        style={isSpecialType ? { cursor: "default" } : {}}
      >
        {activityLog?.type === LocationActivityLogType.PICKUP_FIELD_CHANGE
          ? renderPickupFieldChangeText(activityLog?.details)
          : activityLog?.type === LocationActivityLogType.HEALTH_INDEX_CHANGE
          ? renderHealthIndexChangeText(activityLog?.details)
          : t("view_details")}
      </div>

      {modalOpen && (
        <ModalConfirm
          title="recent_activity"
          cancelLabel={""}
          confirmLabel={"confirm"}
          onClose={handleModalClose}
          onConfirm={handleModalClose}
          color="blue"
          translate={true}
          secondaryDescription={`${activityLog?.location?.name || ""}\n${dayjs(
            activityLog?.activity_date || activityLog?.created_at
          ).format("YYYY-MM-DD HH:mm:ss")}`}
        >
          {renderModalContent()}
        </ModalConfirm>
      )}
    </>
  );
};
